// HomeDashboard — the admin panel's landing screen. Colour tiles for the
// numbers a school office checks first thing (who's in, money in/out, buses,
// alerts), then short "needs attention" lists. Every tile jumps to the
// screen that lets you act on it.
import { useEffect, useState } from 'react';
import {
  Users, UserX, UserCheck, Wallet, AlertCircle, Bus, Siren, CalendarClock,
  ClipboardList, GraduationCap, RefreshCw,
} from 'lucide-react';
import { getDashboard } from '../api/client';
import { Card, CardHeader, StatTile } from '../ui/Card';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { inr, fmtDate, fmtTime } from '../ui/format';

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

export default function HomeDashboard({ onNavigate }) {
  const [data,    setData]    = useState(null);
  const [msg,     setMsg]     = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true); setMsg('');
    try { setData(await getDashboard()); }
    catch (err) { setMsg('Failed to load dashboard: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  }

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div>
      <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h2 className="font-display font-extrabold text-2xl text-ink">{greeting()}</h2>
          <p className="text-sm text-ink-soft mt-0.5">{today}</p>
        </div>
        <Button variant="secondary" onClick={load} disabled={loading} className="flex items-center gap-1.5">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </Button>
      </div>

      {msg && <p className="text-critical text-sm font-bold mb-3">{msg}</p>}
      {!data && loading && <p className="text-ink-soft text-sm">Loading…</p>}

      {data && (
        <>
          {data.open_sos > 0 && (
            <button onClick={() => onNavigate('sos')}
              className="w-full mb-5 flex items-center gap-3 bg-critical text-white rounded-m px-4 py-3 text-left font-bold">
              <Siren size={20} /> {data.open_sos} open SOS alert{data.open_sos > 1 ? 's' : ''} — tap to respond
            </button>
          )}

          <SectionLabel>Today</SectionLabel>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-3.5 mb-6">
            <StatTile tone="transit" icon={Users} label="Total Students" value={data.students.total}
              caption={`${data.staff.total} staff`} onClick={() => onNavigate('students')} />
            <StatTile tone="success" icon={UserCheck} label="Present Today" value={data.students.present_today}
              caption={data.students.not_marked ? `${data.students.not_marked} not marked yet` : 'All classes marked'}
              onClick={() => onNavigate('staffAttendance')} />
            <StatTile tone="critical" icon={UserX} label="Absent Today" value={data.students.absent_today}
              caption={`${data.transport.bus_absent_today} skipping the bus`} onClick={() => onNavigate('attendanceReports')} />
            <StatTile tone="slate" icon={GraduationCap} label="Staff Checked In" value={`${data.staff.present_today}/${data.staff.total}`}
              onClick={() => onNavigate('staffAttendance')} />
          </div>

          <SectionLabel>Fees</SectionLabel>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-3.5 mb-6">
            <StatTile tone="success" icon={Wallet} label="Collected This Month" value={inr(data.fees.collected_this_month)}
              caption={`${inr(data.fees.collected_today)} today`} onClick={() => onNavigate('reports')} />
            <StatTile tone="caution" icon={AlertCircle} label="Pending Dues" value={inr(data.fees.total_pending)}
              caption={`${data.fees.students_with_dues} students`} onClick={() => onNavigate('reports:dues')} />
          </div>

          <SectionLabel>Transport &amp; requests</SectionLabel>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-3.5 mb-6">
            <StatTile tone="transit" icon={Bus} label="Buses Running"
              value={data.transport.trips_running == null ? '—' : `${data.transport.trips_running}/${data.transport.total_routes}`}
              caption={`${data.transport.boarded_today} student${data.transport.boarded_today === 1 ? '' : 's'} boarded today`} onClick={() => onNavigate('routes')} />
            <StatTile tone={data.open_sos ? 'critical' : 'slate'} icon={Siren} label="Open SOS Alerts" value={data.open_sos}
              onClick={() => onNavigate('sos')} />
            <StatTile tone={data.pending_leaves ? 'clearance' : 'slate'} icon={CalendarClock} label="Leave Requests"
              value={data.pending_leaves} caption="awaiting approval" onClick={() => onNavigate('leave')} />
            <StatTile tone="clearance" icon={ClipboardList} label="Open Enquiries" value={data.open_enquiries}
              caption="admissions pipeline" onClick={() => onNavigate('enquiries')} />
          </div>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4">
            <AttentionList title="Leave awaiting approval" empty="No pending leave requests" onMore={() => onNavigate('leave')}
              items={data.lists.leaves.map(l => ({
                key: l.id,
                main: l.applicant_name,
                sub: `${l.leave_type} · ${fmtDate(l.start_date)}${l.end_date !== l.start_date ? ` – ${fmtDate(l.end_date)}` : ''}`,
                badge: <Badge tone="info">{l.applicant_role}</Badge>,
              }))} />
            <AttentionList title="Enquiry follow-ups due" empty="No follow-ups due today" onMore={() => onNavigate('enquiries')}
              items={data.lists.follow_ups.map(e => ({
                key: e.id,
                main: e.student_name,
                sub: `${e.phone}${e.class_sought ? ` · Class ${e.class_sought}` : ''}`,
                badge: <Badge tone="caution">{fmtDate(e.follow_up_date)}</Badge>,
              }))} />
            {data.lists.sos.length > 0 && (
              <AttentionList title="SOS alerts" onMore={() => onNavigate('sos')}
                items={data.lists.sos.map(s => ({
                  key: s.id,
                  main: `${s.driver_name || 'Driver'} — ${s.route_name || 'route'}`,
                  sub: `${fmtDate(s.triggered_at)} ${fmtTime(s.triggered_at)}`,
                  badge: <Badge tone={s.status === 'active' ? 'critical' : 'caution'}>{s.status}</Badge>,
                }))} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SectionLabel({ children }) {
  return <h3 className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-soft mb-2">{children}</h3>;
}

function AttentionList({ title, items, empty, onMore }) {
  return (
    <Card>
      <CardHeader title={title}
        action={<button onClick={onMore} className="text-xs font-bold text-transit-deep hover:underline">View all</button>} />
      {!items.length && <p className="px-5 py-6 text-sm text-ink-soft">{empty}</p>}
      {items.map(it => (
        <div key={it.key} className="px-5 py-3 border-t border-line-soft first:border-t-0 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-ink truncate">{it.main}</div>
            <div className="text-xs text-ink-soft truncate">{it.sub}</div>
          </div>
          {it.badge}
        </div>
      ))}
    </Card>
  );
}
